import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface UnreadContextType {
  unreadCount: number;
  refreshUnread: () => Promise<void>;
}

const UnreadContext = createContext<UnreadContextType>({ unreadCount: 0, refreshUnread: async () => {} });

export const useUnread = () => useContext(UnreadContext);

export const UnreadProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = async () => {
    if (!user) { setUnreadCount(0); return; }

    // Check if user is an agency member
    const { data: agencyId } = await supabase.rpc("get_user_agency_id", { _user_id: user.id });

    if (agencyId) {
      // Agency user: count unread across all managed creators
      const { data: rosterEntries } = await supabase
        .from("agency_roster")
        .select("creator_id")
        .eq("agency_id", agencyId)
        .eq("status", "active");

      const creatorIds = rosterEntries?.map(r => r.creator_id).filter(Boolean) as string[] || [];
      if (creatorIds.length === 0) { setUnreadCount(0); return; }

      const { count } = await supabase
        .from("collab_requests")
        .select("id", { count: "exact", head: true })
        .in("influencer_id", creatorIds)
        .eq("status", "verified")
        .eq("archived", false)
        .is("read_at", null);
      setUnreadCount(count ?? 0);
    } else {
      // Creator: count own unread
      const { count } = await supabase
        .from("collab_requests")
        .select("id", { count: "exact", head: true })
        .eq("influencer_id", user.id)
        .eq("status", "verified")
        .eq("archived", false)
        .is("read_at", null);
      setUnreadCount(count ?? 0);
    }
  };

  useEffect(() => {
    fetchUnread();
  }, [user]);

  // Subscribe to realtime changes on collab_requests
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("unread-badge")
      .on("postgres_changes", { event: "*", schema: "public", table: "collab_requests", filter: `influencer_id=eq.${user.id}` }, () => {
        fetchUnread();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return (
    <UnreadContext.Provider value={{ unreadCount, refreshUnread: fetchUnread }}>
      {children}
    </UnreadContext.Provider>
  );
};
